import React from 'react';
import { ArrowLeft, Download, FileText, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';

const renderText = (text) => {
  if (typeof text !== 'string') return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-foreground">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export const Whitepaper = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('navigation.backToHome')}
          </Button>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_567a6262-0291-4069-9a05-5048d6f99ff3/artifacts/3eapd20t_logo.png"
              alt="Shenbury"
              className="h-8 w-8"
            />
            <span className="text-xl font-serif font-semibold text-gradient-primary">Shenbury</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="glass border-secondary/30 mb-4">
              <FileText className="w-4 h-4 mr-2 inline" />
              {t('whitepaper.researchEdition')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold mb-4">
              {t('whitepaper.title')}
            </h1>
            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span>{t('whitepaper.version')}</span>
              <span>•</span>
              <span>{t('whitepaper.year')}</span>
              <span>•</span>
              <span>Shenbury</span>
            </div>
          </div>

          {/* Abstract */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold">{t('whitepaper.content.abstract.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {t('whitepaper.content.abstract.paragraphs').map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* Problem Statement */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.problemStatement.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {t('whitepaper.content.problemStatement.paragraphs').map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* System Architecture */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.systemArchitecture.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                {t('whitepaper.content.systemArchitecture.intro')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {t('whitepaper.content.systemArchitecture.layers').map((layer) => (
                  <div key={layer.num} className="glass rounded-lg p-4 border border-primary/20">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center font-serif font-bold text-sm">
                        {layer.num}
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">{layer.title}</h4>
                        <p className="text-sm text-muted-foreground">{layer.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AgentDB */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.agentDB.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {t('whitepaper.content.agentDB.paragraphs').map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* ReasoningBank */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.reasoningBank.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {t('whitepaper.content.reasoningBank.paragraphs').map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* Memory Retrieval */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.memoryRetrieval.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{t('whitepaper.content.memoryRetrieval.intro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t('whitepaper.content.memoryRetrieval.items').map((item, i) => (
                  <li key={i}><strong className="text-foreground">{item.term}</strong>: {item.desc}</li>
                ))}
              </ul>
              <p>{t('whitepaper.content.memoryRetrieval.outro')}</p>
            </CardContent>
          </Card>

          {/* Agentic-Flow Architecture */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.agenticFlow.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{renderText(t('whitepaper.content.agenticFlow.intro'))}</p>
              <p>{t('whitepaper.content.agenticFlow.componentsIntro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t('whitepaper.content.agenticFlow.components').map((comp, i) => (
                  <li key={i}><strong className="text-foreground">{comp.term}</strong>: {comp.desc}</li>
                ))}
              </ul>
              <p>{t('whitepaper.content.agenticFlow.outro')}</p>
            </CardContent>
          </Card>

          {/* Claude-Flow Distributed Memory */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.claudeFlow.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{renderText(t('whitepaper.content.claudeFlow.intro'))}</p>
              <p>{t('whitepaper.content.claudeFlow.mechanismsIntro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t('whitepaper.content.claudeFlow.mechanisms').map((mech, i) => (
                  <li key={i}><strong className="text-foreground">{mech.term}</strong>: {mech.desc}</li>
                ))}
              </ul>
              <p>{t('whitepaper.content.claudeFlow.outro')}</p>
            </CardContent>
          </Card>

          {/* Authentication Chain Integration */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.authenticationChain.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{renderText(t('whitepaper.content.authenticationChain.intro'))}</p>
              <p>{t('whitepaper.content.authenticationChain.integrationIntro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t('whitepaper.content.authenticationChain.integrationPoints').map((point, i) => (
                  <li key={i}><strong className="text-foreground">{point.term}</strong>: {point.desc}</li>
                ))}
              </ul>
              <p>{t('whitepaper.content.authenticationChain.outro')}</p>
            </CardContent>
          </Card>

          {/* Security & Risk Mitigation */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.security.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{t('whitepaper.content.security.intro')}</p>
              
              <div className="grid md:grid-cols-2 gap-6 my-6">
                <div className="glass p-4 rounded-lg border border-destructive/20">
                  <h4 className="font-semibold text-foreground mb-3">{t('whitepaper.content.security.threatTitle')}</h4>
                  <ul className="space-y-2 text-sm">
                    {t('whitepaper.content.security.threats').map((threat, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-destructive mt-1.5 flex-shrink-0"></div>
                        <span><strong>{threat.term}:</strong> {threat.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="glass p-4 rounded-lg border border-green-500/20">
                  <h4 className="font-semibold text-foreground mb-3">{t('whitepaper.content.security.resilienceTitle')}</h4>
                  <ul className="space-y-2 text-sm">
                    {t('whitepaper.content.security.resilience').map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
                        <span><strong>{item.term}:</strong> {item.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <p>{t('whitepaper.content.security.outro')}</p>
            </CardContent>
          </Card>

          {/* Roadmap */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.roadmap.heading')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {t('whitepaper.content.roadmap.phases').map((item) => (
                  <div key={item.phase} className="flex items-start gap-4 p-4 rounded-lg glass border border-border">
                    <div className="flex-shrink-0">
                      <Badge variant="outline" className="border-secondary/30 text-secondary">
                        {item.phase}
                      </Badge>
                    </div>
                    <div className="flex-grow">
                      <p className="text-foreground font-medium mb-1">{item.milestone}</p>
                      <Badge variant="outline" className="text-xs">{item.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Competitive Analysis */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{t('whitepaper.content.competitiveAnalysis.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{t('whitepaper.content.competitiveAnalysis.intro')}</p>
              
              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 font-semibold text-foreground">{t('whitepaper.content.competitiveAnalysis.tableHeaders.feature')}</th>
                      <th className="text-left p-3 font-semibold text-foreground">{t('whitepaper.content.competitiveAnalysis.tableHeaders.shenbury')}</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">{t('whitepaper.content.competitiveAnalysis.tableHeaders.traditional')}</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">{t('whitepaper.content.competitiveAnalysis.tableHeaders.basicAI')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {t('whitepaper.content.competitiveAnalysis.tableRows').map((row, i) => (
                      <tr key={i}>
                        <td className="p-3 font-medium">{row.feature}</td>
                        <td className="p-3 text-foreground font-semibold">{row.shenbury}</td>
                        <td className="p-3">{row.traditional}</td>
                        <td className="p-3">{row.basicAI}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <p>{renderText(t('whitepaper.content.competitiveAnalysis.outro'))}</p>
            </CardContent>
          </Card>

          {/* Conclusion */}
          <Card className="glass border-secondary/20 shadow-gold mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold">{t('whitepaper.content.conclusion.heading')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {t('whitepaper.content.conclusion.paragraphs').map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center py-8 border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">
              {t('whitepaper.content.footerCopyright')}
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                {t('common.downloadPDF')}
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => navigate('/technical-paper')}>
                <BookOpen className="w-4 h-4" />
                {t('common.technicalPaper')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
